import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { InputRange, type Payload } from 'components/library/Input/InputRange/InputRange';
import InputText from 'components/library/Input/InputText/InputText';
import CountryDropdown from 'components/library/Input/CountryDropdown/index';
import Dropdown from 'components/library/Input/Dropdown/Dropdown';
import MultipleChoiceField from 'components/library/Input/MultipleChoiceField/MultipleChoiceField';
import Button from 'components/library/Button/Button';

import styles from './Simulation.module.css';
import getInteger from 'utils/number';
import isEmpty from 'utils/isEmpty';
import { isSiren } from 'utils/isSiren';

type Error = {
  siren?: string;
};

interface Props {
  amountSought: number;
  country: string;
  siren: string;
  typeInvestment: number | null;
  amountCA: number;
  amountRN: number;
  handleSubmit: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

export default function Simulation({
  amountSought, country, siren, typeInvestment, amountCA, amountRN, handleSubmit,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [formattedValue, setFormattedValue] = useState<string | number>(amountSought);
  const [isComplete, setIsComplete] = useState(false);
  const [errors, setErrors] = useState<Error>({});

  useEffect(() => {
    const newErrors: Error = {};
    if (amountSought && (!Number.isNaN(amountSought))) {
      setFormattedValue(getInteger(amountSought).toLocaleString('en').replaceAll(',', ' '));
    }
    let correctRegistrationNumber = true;
    if (siren && (siren.length > 20 || (country === 'FR' && !isSiren(siren)))) {
      correctRegistrationNumber = false;
      if (country === 'FR') {
        newErrors.siren = t('eligible.simulation.sirenError');
      } else {
        newErrors.siren = t('eligible.simulation.registrationNumberError');
      }
    }
    const toCheck = [
      amountSought, country, siren,
      typeInvestment !== null, amountCA !== null, amountRN !== null,
    ];
    const completed = toCheck.map((i) => !isEmpty(i)).reduce((c, a) => {
      return c && a
    });
    setIsComplete(completed && correctRegistrationNumber);
    if (Object.keys(newErrors).length > 0) setErrors(newErrors);
    else setErrors({});
  }, [amountSought, country, siren, typeInvestment, amountCA, amountRN, setFormattedValue]);

  const handleAmountSought = (e: Payload) => {
    setFormattedValue(getInteger(e.target.value).toLocaleString('en').replaceAll(',', ' '));
    dispatch({ type: 'UPDATE_AMOUNT_SOUGHT', payload: e.target.value });
  };

  return (
    <section className={styles.simulation}>
      <div className={styles.content}>
        <div className={styles.containerAmountSought}>
          <InputRange
            label={t('eligible.simulation.amountSought')}
            value={formattedValue}
            onChange={handleAmountSought}
            min={20000}
            max={1500000}
            step={10000}
            minLabel="20 000 EUR"
            maxLabel="1 500 000 EUR"
            suffixValue="€"
          />
        </div>
        <div className={styles.containerCompany}>
          <CountryDropdown
            label={t('eligible.simulation.country')}
            className={styles.countryCompany}
            value={country || ''}
            onChange={(e) => dispatch({ type: 'UPDATE_COUNTRY', payload: e.target.value })}
          />
          <InputText
            className={styles.sirenCompany}
            label={t('eligible.simulation.siren')}
            valueInput={siren}
            onTextChange={(e) => dispatch({ type: 'UPDATE_SIREN', payload: e.target.value })}
            state={(errors?.siren) ? 'ERROR' : 'DEFAULT'}
            hint={errors?.siren}
            required
          />
        </div>
        <div className={styles.containerTypeInvestment}>
          <MultipleChoiceField
            className={styles.choiceButton}
            label={t('eligible.simulation.typeInvestment')}
            selectedId={typeInvestment}
            options={[
              {
                id: 0,
                icon: (<i className="material-icons">show_chart</i>),
                text: t('eligible.simulation.capitalIncreasing'),
              },
              {
                id: 1,
                icon: (<i className="material-icons">credit_card</i>),
                text: t('eligible.simulation.investment'),
              },
              {
                id: 2,
                icon: (<i className="material-icons">savings</i>),
                text: t('eligible.simulation.treasury'),
              },
              {
                id: 3,
                icon: (<i className="material-icons">account_balance</i>),
                text: t('eligible.simulation.workingCapital'),
              },
              {
                id: 4,
                icon: (<i className="material-icons">shopping_cart</i>),
                text: t('eligible.simulation.commercialProperty'),
              },
              {
                id: 5,
                icon: (<i className="material-icons">local_hospital</i>),
                text: t('eligible.simulation.otherNeeds'),
              },
            ]}
            onChange={(value) => dispatch({ type: 'UPDATE_TYPE_INVESTMENT', payload: value })}
            required
          />
        </div>
        <div className={styles.containerCA}>
          <Dropdown
            label={t('eligible.simulation.CA', { year: new Date().getFullYear() - 1 })}
            selected={amountCA}
            options={[
              {
                value: 0,
                label: t('eligible.simulation.0to150'),
              },
              {
                value: 1,
                label: t('eligible.simulation.150to300'),
              },
              {
                value: 2,
                label: t('eligible.simulation.300to500'),
              },
              {
                value: 3,
                label: t('eligible.simulation.500to1'),
              },
              {
                value: 4,
                label: t('eligible.simulation.moreThan1'),
              },
            ]}
            onChange={(value) => dispatch({ type: 'UPDATE_AMOUNT_CA', payload: value })}
            required
          />
          <Dropdown
            label={t('eligible.simulation.RN', { year: new Date().getFullYear() - 1 })}
            selected={amountRN}
            options={[
              {
                value: 0,
                label: t('eligible.simulation.lessThan0'),
              },
              {
                value: 1,
                label: t('eligible.simulation.0to150'),
              },
              {
                value: 2,
                label: t('eligible.simulation.150to300'),
              },
              {
                value: 3,
                label: t('eligible.simulation.300to500'),
              },
              {
                value: 4,
                label: t('eligible.simulation.500to1'),
              },
              {
                value: 5,
                label: t('eligible.simulation.moreThan1'),
              },
            ]}
            onChange={(value) => dispatch({ type: 'UPDATE_AMOUNT_RN', payload: value })}
            required
          />
        </div>
        <Button
          className={styles.submitButton}
          variant="fill"
          type="secondary"
          label={t('eligible.simulation.submit')}
          icon={<i className="material-icons">arrow_forward</i>}
          disabled={!isComplete}
          onButtonClick={handleSubmit}
        />
      </div>
    </section>
  );
}
